import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { withdrawConsultantBalance } from "../../services/api/consultantsPayout";

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * WithdrawDialog — isolates the modal's amount/reference local state from the
 * parent ConsultantsPayout page. Without this split, each keystroke on the
 * amount input would re-render the full payout table (100+ rows) causing 1-2s
 * input lag. Keeping form state inside this child means typing only re-renders
 * the dialog itself.
 *
 * Parent owns: which consultant is targeted + open/close.
 * This component owns: amount, reference, submitting, validation.
 */
const WithdrawDialog = ({ open, consultant, onClose, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset fields when dialog re-opens for a different consultant (or after a
  // successful submit followed by re-open).
  useEffect(() => {
    if (open) {
      setAmount("");
      setReference("");
      setSubmitting(false);
    }
  }, [open, consultant?.id]);

  const handleSubmit = async () => {
    if (!consultant?.id) return;
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter a valid positive amount", { position: "top-center" });
      return;
    }
    if (amt > Number(consultant.balance || 0)) {
      toast.error(
        `Cannot withdraw more than current balance (${inr(consultant.balance)})`,
        { position: "top-center" },
      );
      return;
    }
    setSubmitting(true);
    try {
      const res = await withdrawConsultantBalance({
        id: consultant.id,
        amount: amt,
        reference_id: reference?.trim() || undefined,
        description: `Payout to consultant ${consultant.name ?? consultant.id}`,
      });
      if (res?.data?.status === "success") {
        toast.success(
          `₹${amt.toLocaleString("en-IN")} withdrawn from ${consultant.name ?? consultant.id}`,
          { position: "top-center" },
        );
        onSuccess?.();
        onClose?.();
      } else {
        toast.error(res?.data?.message || "Withdrawal failed", {
          position: "top-center",
        });
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Withdrawal failed", {
        position: "top-center",
      });
      setSubmitting(false);
    }
  };

  const submitDisabled =
    submitting ||
    !amount ||
    Number(amount) <= 0 ||
    Number(amount) > Number(consultant?.balance || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Withdraw from {consultant?.name || "consultant"}
      </DialogTitle>
      <DialogContent>
        <div className="withdraw-meta">
          <div className="withdraw-meta-row">
            <span>Current balance:</span>
            <b className="withdraw-balance">{inr(consultant?.balance)}</b>
          </div>
          <div className="withdraw-meta-row">
            <span>Bank / Account:</span>
            <span>
              {consultant?.bank_name || "—"}
              {consultant?.account_number ? ` · ${consultant.account_number}` : ""}
            </span>
          </div>
          <div className="withdraw-meta-row">
            <span>IFSC:</span>
            <span>{consultant?.ifsc || "—"}</span>
          </div>
        </div>

        <TextField
          autoFocus
          fullWidth
          margin="normal"
          label="Withdraw amount (₹)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{
            min: 1,
            max: Number(consultant?.balance || 0),
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Reference / UTR (optional)"
          placeholder="Bank UTR or payout reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={submitDisabled}>
          {submitting ? "Processing…" : "Submit Withdrawal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(WithdrawDialog);
